import React, { useState, useEffect, useContext } from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import { AdminService } from '../../../services/AdminService';
import UserContext from '../../../context/UserContext';

interface Client {
  _id: string;
  firstname: string;
  lastname: string;
  email: string;
  dateOfBirth?: string | null;
  gender?: string;
  state?: string | null;
  zipCode?: string | null;
}

interface Form {
  formTitle: string;
  formId: string;
}

const FormLinkGenerator: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [searchResults, setSearchResults] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [user] = useContext(UserContext);

  // Fetch enabled forms when component mounts
  useEffect(() => {
    const fetchEnabledForms = async () => {
      try {
        setIsLoading(true);
        const response = await AdminService.getEnabledForms();
        if (response.success && response.data) {
          setForms(response.data.map(form => ({
            formId: form.form_id,
            formTitle: form.title_form
          })));
        } else {
          toast.error("Failed to load forms");
        }
      } catch (error) {
        toast.error("Error loading forms");
      } finally {
        setIsLoading(false);
      }
    };

    fetchEnabledForms();
  }, []);

  // Generate form link
  const generateFormLink = () => {
    if (!selectedForm) {
      toast.error("Please select a form");
      return;
    }

    const baseUrl = `https://form.jotform.com/${selectedForm.formId}`;
    
    if (selectedClient) {
      const therapistId = user?._id || 'admin';
      const clientId = selectedClient._id;
      const queryParams = `?hash_therapist=${therapistId}&hash_clientid=${clientId}`;
      setGeneratedLink(`${baseUrl}${queryParams}`);
    } else {
      setGeneratedLink(baseUrl);
    }
  };

  // Search clients by name
  const searchClients = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    try {
      const response = await AdminService.searchClientsByName(query);
      if (response.success && response.data && response.data.clients) {
        setSearchResults(response.data.clients);
        setShowResults(true);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      setSearchResults([]);
    }
  };

  // Handle client selection
  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setSearchTerm(`${client.firstname} ${client.lastname}`);
    setShowResults(false);
    // Clear the generated link when selecting a new client
    setGeneratedLink('');
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    if (!generatedLink) return;
    
    navigator.clipboard.writeText(generatedLink)
      .then(() => {
        toast.success("Link copied to clipboard!", {
          position: toast.POSITION.TOP_RIGHT
        });
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        toast.error("Failed to copy link");
      });
  };

  // Send email invitation
  const sendEmailInvitation = async () => {
    if (!generatedLink || !selectedClient) return;
    
    try {
      setIsLoading(true);
      const response = await AdminService.sendJotformInvitation({
        clientId: selectedClient._id,
        formLink: generatedLink
      });
      
      if (response.success) {
        toast.success("Form invitation sent successfully!", {
          position: toast.POSITION.TOP_RIGHT
        });
      } else {
        toast.error(response.error || "Failed to send form invitation", {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    } catch (error) {
      console.error("Error sending form invitation:", error);
      toast.error("Failed to send form invitation", {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(145deg, #f8f9fa, #ffffff)',
      padding: '30px',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e9ecef',
      marginBottom: '30px'
    }}>
      <div>
        <h4 style={{ 
          color: '#2c3e50', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '20px'
        }}>
          <span style={{
            background: '#f97316',
            color: 'white',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="bi bi-link-45deg"></i>
          </span>
          Form Link Generator
        </h4>
        
        <Row>
          <Col md={4} style={{position: 'relative'}}>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Search clients by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                searchClients(e.target.value);
              }}
              style={{
                height: '50px',
                fontSize: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '0.5rem'
              }}
            />

            {showResults && searchResults.length > 0 && (
              <div style={{
                position: 'absolute',
                width: '100%',
                maxHeight: '200px',
                overflowY: 'auto',
                background: 'white',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                border: '1px solid #e5e7eb'
              }}>
                {searchResults.map((client) => (
                  <div 
                    key={client._id} 
                    onClick={() => handleClientSelect(client)}
                    style={{
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background-color 0.2s',
                      fontSize: '15px'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {client.firstname} {client.lastname}
                  </div>
                ))}
              </div>
            )}
          </Col>

          <Col md={4}>
            <select
              className="form-select form-select-lg"
              onChange={(e) => {
                const selected = forms.find(form => form.formId === e.target.value);
                setSelectedForm(selected || null);
                // Clear the generated link when selecting a different form
                setGeneratedLink('');
              }}
              style={{
                height: '50px',
                fontSize: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '16px 12px'
              }}
            >
              <option value="">Select a form...</option>
              {forms.map((form) => (
                <option key={form.formId} value={form.formId}>
                  {form.formTitle}
                </option>
              ))}
            </select>
          </Col>

          <Col md={4}>
            <button
              className="btn btn-primary btn-lg w-100"
              onClick={generateFormLink}
              disabled={!selectedForm}
              style={{
                height: '50px',
                background: 'linear-gradient(to right, #f97316, #fb923c)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.25)',
                fontSize: '16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <i className="bi bi-link-45deg"></i> Generate Link
            </button>
          </Col>
        </Row>
        
        {generatedLink && (
          <div style={{marginTop: '1.5rem'}}>
            <div style={{display: 'flex'}}>
              <input
                type="text"
                className="form-control form-control-lg"
                value={generatedLink}
                readOnly
                style={{ 
                  height: '50px', 
                  fontSize: '16px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px 0 0 8px',
                  backgroundColor: '#f8f9fa',
                  paddingLeft: '16px',
                  color: '#495057',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
              <button
                className="btn btn-outline-primary"
                onClick={copyToClipboard}
                style={{
                  height: '50px',
                  borderColor: '#f97316',
                  color: '#f97316',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  borderRadius: selectedClient ? '0' : '0 8px 8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="bi bi-clipboard"></i> Copy
              </button>
              {selectedClient && (
                <button
                  className="btn btn-primary"
                  onClick={sendEmailInvitation}
                  disabled={isLoading}
                  style={{
                    height: '50px',
                    background: '#2979ff',
                    border: 'none',
                    borderRadius: '0 8px 8px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(41, 121, 255, 0.25)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-envelope"></i>
                      Send Email
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormLinkGenerator;
